import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  People,
  Store,
  LocalOffer,
  TrendingUp,
  CheckCircle,
  Warning,
  Person,
  Business,
  Schedule,
  Star,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

interface DashboardStats {
  totalUsers: number;
  totalVendors: number;
  totalOffers: number;
  pendingApprovals: number;
  activeUsers: number;
  revenue: number;
}

interface RecentActivity {
  id: string;
  type: 'user' | 'vendor' | 'offer';
  action: string;
  timestamp: string;
  status: 'success' | 'warning' | 'error';
}

const DashboardHome: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats>({
    totalUsers: 0,
    totalVendors: 0,
    totalOffers: 0,
    pendingApprovals: 0,
    activeUsers: 0,
    revenue: 0,
  });

  const [recentActivities, setRecentActivities] = useState<RecentActivity[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Fetch dashboard data
    const fetchDashboardData = async () => {
      try {
        // Fetch real data from backend API
        const token = localStorage.getItem('token');
        const [statsRes, activitiesRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_ADMIN_URL || 'https://godiscountdays.com/api/admin'}/stats`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }),
          fetch(`${process.env.REACT_APP_ADMIN_URL || 'https://godiscountdays.com/api/admin'}/activities/recent`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }),
        ]);

        const statsData = await statsRes.json();
        const activitiesData = await activitiesRes.json();

        if (statsData.success && statsData.data) {
          setStats({
            totalUsers: statsData.data.totalUsers || 0,
            totalVendors: statsData.data.totalVendors || 0,
            totalOffers: statsData.data.totalOffers || 0,
            pendingApprovals: statsData.data.pendingVendors || 0,
            activeUsers: statsData.data.activeUsers || 0,
            revenue: 0, // Will be calculated from analytics
          });
        } else {
          console.warn('Stats API returned unsuccessful response:', statsData);
        }

        if (activitiesData.success && activitiesData.data) {
          // The API returns data.activities array
          const activities = activitiesData.data.activities || [];
          
          // Format timestamps to be more readable
          const formattedActivities = activities.map((activity: any) => ({
            ...activity,
            timestamp: activity.timestamp ? new Date(activity.timestamp).toLocaleString() : 'Unknown time'
          }));
          
          setRecentActivities(formattedActivities);
        } else {
          console.warn('Activities API returned unsuccessful response:', activitiesData);
          // Set fallback data
          setRecentActivities([
            {
              id: '1',
              type: 'vendor',
              action: 'New vendor registration: "Pizza Palace"',
              timestamp: '2 hours ago',
              status: 'warning',
            },
            {
              id: '2',
              type: 'user',
              action: 'User "john@example.com" created account',
              timestamp: '3 hours ago',
              status: 'success',
            },
            {
              id: '3',
              type: 'offer',
              action: 'New offer created: "50% off Pizza"',
              timestamp: '4 hours ago',
              status: 'success',
            },
          ]);
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        // Set default data for demo
        setStats({
          totalUsers: 1250,
          totalVendors: 340,
          totalOffers: 2100,
          pendingApprovals: 12,
          activeUsers: 890,
          revenue: 45600,
        });
        setRecentActivities([
          {
            id: '1',
            type: 'vendor',
            action: 'New vendor registration: "Pizza Palace"',
            timestamp: '2 hours ago',
            status: 'warning',
          },
          {
            id: '2',
            type: 'user',
            action: 'User "john@example.com" created account',
            timestamp: '3 hours ago',
            status: 'success',
          },
          {
            id: '3',
            type: 'offer',
            action: 'New offer created: "50% off Pizza"',
            timestamp: '4 hours ago',
            status: 'success',
          },
        ]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success':
        return 'success';
      case 'warning':
        return 'warning';
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'user':
        return <Person />;
      case 'vendor':
        return <Business />;
      case 'offer':
        return <LocalOffer />;
      default:
        return <Person />;
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <Typography>Loading dashboard...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: { xs: 3, md: 4 } }}>
        <Typography 
          variant="h4" 
          component="h1" 
          sx={{ 
            fontWeight: 600, 
            color: '#3B3B3B', 
            mb: 1,
            fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' },
          }}
        >
          Dashboard Overview
        </Typography>
        <Typography 
          variant="body1" 
          color="text.secondary"
          sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
        >
          Welcome to the DiscountDays admin panel. Here's what's happening with your platform.
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, sm: 3 }, mb: 4 }}>
        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 250px' }, minWidth: { xs: '100%', sm: '200px', md: '250px' } }}>
          <Card 
            onClick={() => navigate('/admin/users')}
            sx={{ 
              borderRadius: 3,
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              border: '1px solid #e0e0e0',
              background: 'linear-gradient(135deg, #3B3B3B 0%, #9A8174 100%)',
              color: 'white',
              position: 'relative',
              overflow: 'hidden',
              cursor: 'pointer',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
                transition: 'all 0.3s ease-in-out',
              },
            }}
          >
            <Box sx={{ position: 'absolute', top: -20, right: -20, opacity: 0.1 }}>
              <People sx={{ fontSize: 80 }} />
            </Box>
            <CardContent sx={{ position: 'relative', zIndex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <People sx={{ mr: 1, fontSize: 28 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>Total Users</Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                {stats.totalUsers.toLocaleString()}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <TrendingUp sx={{ mr: 0.5, fontSize: 16 }} />
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  +12% from last month
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 250px' }, minWidth: { xs: '100%', sm: '200px', md: '250px' } }}>
          <Card 
            onClick={() => navigate('/admin/vendors')}
            sx={{ 
              borderRadius: 3,
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              border: '1px solid #e0e0e0',
              background: 'linear-gradient(135deg, #4CAF50 0%, #8BC34A 100%)',
              color: 'white',
              position: 'relative',
              overflow: 'hidden',
              cursor: 'pointer',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
                transition: 'all 0.3s ease-in-out',
              },
            }}
          >
            <Box sx={{ position: 'absolute', top: -20, right: -20, opacity: 0.1 }}>
              <Store sx={{ fontSize: 80 }} />
            </Box>
            <CardContent sx={{ position: 'relative', zIndex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Store sx={{ mr: 1, fontSize: 28 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>Total Vendors</Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                {stats.totalVendors.toLocaleString()}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <TrendingUp sx={{ mr: 0.5, fontSize: 16 }} />
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  +8% from last month
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 250px' }, minWidth: { xs: '100%', sm: '200px', md: '250px' } }}>
          <Card 
            onClick={() => navigate('/admin/offers')}
            sx={{ 
              borderRadius: 3,
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              border: '1px solid #e0e0e0',
              background: 'linear-gradient(135deg, #FF9800 0%, #FFC107 100%)',
              color: 'white',
              position: 'relative',
              overflow: 'hidden',
              cursor: 'pointer',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
                transition: 'all 0.3s ease-in-out',
              },
            }}
          >
            <Box sx={{ position: 'absolute', top: -20, right: -20, opacity: 0.1 }}>
              <LocalOffer sx={{ fontSize: 80 }} />
            </Box>
            <CardContent sx={{ position: 'relative', zIndex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LocalOffer sx={{ mr: 1, fontSize: 28 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>Active Offers</Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                {stats.totalOffers.toLocaleString()}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <TrendingUp sx={{ mr: 0.5, fontSize: 16 }} />
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  +15% from last month
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 250px' }, minWidth: { xs: '100%', sm: '200px', md: '250px' } }}>
          <Card 
            onClick={() => navigate('/admin/vendor-approval')}
            sx={{ 
              borderRadius: 3,
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              border: '1px solid #e0e0e0',
              background: 'linear-gradient(135deg, #F44336 0%, #E91E63 100%)',
              color: 'white',
              position: 'relative',
              overflow: 'hidden',
              cursor: 'pointer',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
                transition: 'all 0.3s ease-in-out',
              },
            }}
          >
            <Box sx={{ position: 'absolute', top: -20, right: -20, opacity: 0.1 }}>
              <Warning sx={{ fontSize: 80 }} />
            </Box>
            <CardContent sx={{ position: 'relative', zIndex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Warning sx={{ mr: 1, fontSize: 28 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>Pending Approvals</Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                {stats.pendingApprovals}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Schedule sx={{ mr: 0.5, fontSize: 16 }} />
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Requires attention
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, sm: 3 } }}>
        {/* Recent Activities */}
        <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 400px' }, minWidth: { xs: '100%', md: '400px' } }}>
          <Card sx={{ 
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            border: '1px solid #e0e0e0',
            height: '100%',
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Box sx={{ 
                  width: 40, 
                  height: 40, 
                  borderRadius: 2, 
                  backgroundColor: '#3B3B3B', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  mr: 2,
                }}>
                  <Schedule sx={{ color: 'white' }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#3B3B3B' }}>
                  Recent Activities
                </Typography>
              </Box>
              <List sx={{ p: 0 }}>
                {recentActivities && Array.isArray(recentActivities) && recentActivities.length > 0 ? (
                  recentActivities.map((activity, index) => {
                    const getNavigationPath = () => {
                      if (activity.type === 'user') return '/admin/users';
                      if (activity.type === 'vendor') return '/admin/vendors';
                      if (activity.type === 'offer') return '/admin/offers';
                      return '/admin';
                    };

                    return (
                      <ListItem key={activity.id} sx={{ 
                        px: 0, 
                        py: 0,
                        borderBottom: index < recentActivities.length - 1 ? '1px solid #f0f0f0' : 'none',
                      }}>
                        <ListItemButton 
                          onClick={() => navigate(getNavigationPath())}
                          sx={{
                            borderRadius: 1,
                            '&:hover': {
                              backgroundColor: '#f5f5f5',
                            },
                          }}
                        >
                          <ListItemAvatar>
                            <Avatar sx={{ 
                              bgcolor: activity.type === 'user' ? '#4CAF50' : 
                                      activity.type === 'vendor' ? '#FF9800' : '#2196F3',
                              width: 40,
                              height: 40,
                            }}>
                              {getActivityIcon(activity.type)}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Typography variant="body2" sx={{ fontWeight: 500, color: '#333' }}>
                                {activity.action}
                              </Typography>
                            }
                            secondary={
                              <Typography variant="caption" color="text.secondary">
                                {activity.timestamp}
                              </Typography>
                            }
                          />
                          <Chip
                            label={activity.status}
                            color={getStatusColor(activity.status) as any}
                            size="small"
                            sx={{ 
                              fontWeight: 500,
                              textTransform: 'capitalize',
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    );
                  })
                ) : (
                  <ListItem sx={{ px: 0, py: 4 }}>
                    <ListItemText
                      primary={
                        <Typography variant="body1" sx={{ textAlign: 'center', color: 'text.secondary' }}>
                          No recent activities
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary' }}>
                          Check back later for updates
                        </Typography>
                      }
                    />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </Card>
        </Box>

        {/* Quick Actions */}
        <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 400px' }, minWidth: { xs: '100%', md: '400px' } }}>
          <Card sx={{ 
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            border: '1px solid #e0e0e0',
            height: '100%',
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Box sx={{ 
                  width: 40, 
                  height: 40, 
                  borderRadius: 2, 
                  backgroundColor: '#4CAF50', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  mr: 2,
                }}>
                  <CheckCircle sx={{ color: 'white' }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#3B3B3B' }}>
                  Quick Actions
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<CheckCircle />}
                  fullWidth
                  onClick={() => navigate('/admin/vendor-approval')}
                  sx={{ 
                    justifyContent: 'flex-start',
                    py: 1.5,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #3B3B3B 0%, #9A8174 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #050206 0%, #3B3B3B 100%)',
                    },
                  }}
                >
                  Review Pending Vendors
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<People />}
                  fullWidth
                  onClick={() => navigate('/admin/users')}
                  sx={{ 
                    justifyContent: 'flex-start',
                    py: 1.5,
                    borderRadius: 2,
                    borderColor: '#3B3B3B',
                    color: '#3B3B3B',
                    '&:hover': {
                      borderColor: '#050206',
                      backgroundColor: 'rgba(59, 59, 59, 0.04)',
                    },
                  }}
                >
                  Manage Users
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<LocalOffer />}
                  fullWidth
                  onClick={() => navigate('/admin/offers')}
                  sx={{ 
                    justifyContent: 'flex-start',
                    py: 1.5,
                    borderRadius: 2,
                    borderColor: '#3B3B3B',
                    color: '#3B3B3B',
                    '&:hover': {
                      borderColor: '#050206',
                      backgroundColor: 'rgba(59, 59, 59, 0.04)',
                    },
                  }}
                >
                  Review Offers
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<TrendingUp />}
                  fullWidth
                  onClick={() => navigate('/admin/analytics')}
                  sx={{ 
                    justifyContent: 'flex-start',
                    py: 1.5,
                    borderRadius: 2,
                    borderColor: '#3B3B3B',
                    color: '#3B3B3B',
                    '&:hover': {
                      borderColor: '#050206',
                      backgroundColor: 'rgba(59, 59, 59, 0.04)',
                    },
                  }}
                >
                  View Analytics
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Top Performing Vendors */}
        <Box sx={{ flex: '1 1 100%', minWidth: '100%' }}>
          <Card sx={{ 
            borderRadius: 3,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            border: '1px solid #e0e0e0',
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Box sx={{ 
                  width: 40, 
                  height: 40, 
                  borderRadius: 2, 
                  backgroundColor: '#FF9800', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  mr: 2,
                }}>
                  <Star sx={{ color: 'white' }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#3B3B3B' }}>
                  Top Performing Vendors
                </Typography>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                      <TableCell sx={{ fontWeight: 600, color: '#495057' }}>Vendor Name</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#495057' }}>Category</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#495057' }}>Active Offers</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#495057' }}>Revenue</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#495057' }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}>
                      <TableCell sx={{ fontWeight: 500 }}>Pizza Palace</TableCell>
                      <TableCell>Restaurant</TableCell>
                      <TableCell>15</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#4CAF50' }}>$2,450</TableCell>
                      <TableCell>
                        <Chip label="Active" color="success" size="small" sx={{ fontWeight: 500 }} />
                      </TableCell>
                    </TableRow>
                    <TableRow sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}>
                      <TableCell sx={{ fontWeight: 500 }}>Fashion Store</TableCell>
                      <TableCell>Retail</TableCell>
                      <TableCell>12</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#4CAF50' }}>$1,890</TableCell>
                      <TableCell>
                        <Chip label="Active" color="success" size="small" sx={{ fontWeight: 500 }} />
                      </TableCell>
                    </TableRow>
                    <TableRow sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}>
                      <TableCell sx={{ fontWeight: 500 }}>Tech Solutions</TableCell>
                      <TableCell>Service</TableCell>
                      <TableCell>8</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#4CAF50' }}>$1,200</TableCell>
                      <TableCell>
                        <Chip label="Active" color="success" size="small" sx={{ fontWeight: 500 }} />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
};

export default DashboardHome;
